import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {QrCode, DollarSign, User, MapPin, CreditCard, Printer, Clock, AlertTriangle} from 'lucide-react'
import toast from "react-hot-toast"
import { useGuias } from "../context/GuiasContext"
import QRCodeLib from "qrcode"
import { gerarPixPayload } from "../utils/pixQrCode"
import { lumi } from "../lib/lumi"

export default function GerarPagamentos() {
  const { addPagamento, dadosPrefeitura } = useGuias()
  const [nomePagador, setNomePagador] = useState("")
  const [cpfPagador, setCpfPagador] = useState("")
  const [referenciaPagamento, setReferenciaPagamento] = useState("")
  const [valorPagamento, setValorPagamento] = useState("")
  const [dataVencimento, setDataVencimento] = useState("")
  const [comprovanteGerado, setComprovanteGerado] = useState<any>(null)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("")
  const [tempoRestante, setTempoRestante] = useState<string>("")
  const [isNFSe, setIsNFSe] = useState(false)
  const [numeroNotaFiscal, setNumeroNotaFiscal] = useState("")
  const [isTLL, setIsTLL] = useState(false)
  const [valorTLL, setValorTLL] = useState("")
  const [isTFF, setIsTFF] = useState(false)
  const [valorTFF, setValorTFF] = useState("")

  const formatarValor = (valor: string) => {
    // Remove tudo exceto números
    const apenasNumeros = valor.replace(/\D/g, "")
    
    if (!apenasNumeros) return ""
    
    // Se for apenas números sem vírgula, adiciona ,00
    if (!valor.includes(",")) {
      return apenasNumeros + ",00"
    }
    
    return valor
  }

  const formatarCPFouCNPJ = (valor: string) => {
    const numeros = valor.replace(/\D/g, "")
    
    // CPF: 11 dígitos → 000.000.000-00
    if (numeros.length <= 11) {
      return numeros
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    }
    // CNPJ: 14 dígitos → 00.000.000/0000-00
    else if (numeros.length <= 14) {
      return numeros
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
    }
    
    return valor
  }

  const handleCPFouCNPJChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatado = formatarCPFouCNPJ(e.target.value)
    setCpfPagador(formatado)
    
    // Buscar no cadastro geral e vendedor quando CPF/CNPJ estiver completo
    const numeros = formatado.replace(/\D/g, "")
    if (numeros.length === 11 || numeros.length === 14) {
      await buscarCadastroPorCPFouCNPJ(formatado)
    }
  }
  
  const buscarCadastroPorCPFouCNPJ = async (cpfCnpj: string) => {
    try {
      // Primeiro, buscar no cadastro geral
      const { list: cadastrosGerais } = await lumi.entities.cadastro_geral.list({
        filter: { cpfCnpj: cpfCnpj }
      })
      
      if (cadastrosGerais && cadastrosGerais.length > 0) {
        const cadastro = cadastrosGerais[0]
        setNomePagador(cadastro.nomeCompleto)
        toast.success("Cadastro encontrado! Nome preenchido automaticamente.")
        return
      }
      
      // Se não encontrou no cadastro geral, buscar nos vendedores
      const { list: vendedores } = await lumi.entities.vendedores.list({
        filter: { cpfCnpj: cpfCnpj }
      })
      
      if (vendedores && vendedores.length > 0) {
        const vendedor = vendedores[0]
        
        // Preencher nome do pagador
        setNomePagador(vendedor.nomeCompleto)
        
        // Montar referência do pagamento com ramos e taxas
        let referencia = ""
        
        // Adicionar ramos de atividade
        if (vendedor.ramosAtividade && vendedor.ramosAtividade.length > 0) {
          referencia = vendedor.ramosAtividade.join(", ")
        }
        
        // Adicionar taxas aplicadas
        const taxasAplicadas = []
        if (vendedor.taxaNossaSenhora) {
          taxasAplicadas.push("Taxa Nossa Senhora da Luz")
        }
        if (vendedor.taxaFestaSaoJose) {
          taxasAplicadas.push("Taxa Festa de São José do Seridó")
        }
        
        if (taxasAplicadas.length > 0) {
          if (referencia) {
            referencia += " - " + taxasAplicadas.join(", ")
          } else {
            referencia = taxasAplicadas.join(", ")
          }
        }
        
        setReferenciaPagamento(referencia)
        
        // Calcular valor total das taxas
        let valorTotal = 0
        
        if (vendedor.taxaNossaSenhora && vendedor.valorTaxaNossaSenhora) {
          // Remover "R$" e converter para número
          const valorLimpo = vendedor.valorTaxaNossaSenhora.replace(/[^0-9,]/g, "")
          valorTotal += parseFloat(valorLimpo.replace(",", "."))
        }
        
        if (vendedor.taxaFestaSaoJose && vendedor.valorTaxaFestaSaoJose) {
          // Remover "R$" e converter para número
          const valorLimpo = vendedor.valorTaxaFestaSaoJose.replace(/[^0-9,]/g, "")
          valorTotal += parseFloat(valorLimpo.replace(",", "."))
        }
        
        if (valorTotal > 0) {
          // Formatar valor com vírgula
          const valorFormatado = valorTotal.toFixed(2).replace(".", ",")
          setValorPagamento(valorFormatado)
        }
        
        toast.success("Vendedor encontrado! Dados preenchidos automaticamente.")
      }
    } catch (error) {
      console.error("Erro ao buscar cadastro:", error)
    }
  }
  
  const buscarCadastroPorNome = async (nome: string) => {
    if (nome.length < 3) return
    
    try {
      // Buscar no cadastro geral por nome parcial
      const { list } = await lumi.entities.cadastro_geral.list()
      
      const cadastroEncontrado = list.find((c: any) => 
        c.nomeCompleto.toLowerCase().includes(nome.toLowerCase())
      )
      
      if (cadastroEncontrado) {
        setNomePagador(cadastroEncontrado.nomeCompleto)
        setCpfPagador(cadastroEncontrado.cpfCnpj)
        toast.success("Cadastro encontrado! Dados preenchidos automaticamente.")
      }
    } catch (error) {
      console.error("Erro ao buscar por nome:", error)
    }
  }

  const handleGerarPagamento = async () => {
    if (!nomePagador || !cpfPagador || !referenciaPagamento || !valorPagamento || !dataVencimento) {
      toast.error("Preencha todos os campos")
      return
    }
    
    if (isNFSe && !numeroNotaFiscal) {
      toast.error("Preencha o número da Nota Fiscal")
      return
    }
    
    if (isTLL && !valorTLL) {
      toast.error("Preencha o valor da taxa TLL")
      return
    }
    
    if (isTFF && !valorTFF) {
      toast.error("Preencha o valor da taxa TFF")
      return
    }
    
    if (!dadosPrefeitura) {
      toast.error("Dados da prefeitura não configurados")
      return
    }
    
    // Remover "R$ " do valor para salvar apenas xx,xx
    const valorLimpo = valorPagamento.replace(/R\$\s*/g, "").trim()
    const numeroComprovante = `PAG${Date.now().toString().slice(-8)}`
    
    // Usar data de vencimento selecionada (definir para fim do dia - 23:59:59)
    const dataExpiracao = new Date(dataVencimento)
    dataExpiracao.setHours(23, 59, 59, 999)
    
    // Calcular horas de validade (diferença entre agora e data de expiração)
    const agora = new Date()
    const horasValidade = Math.ceil((dataExpiracao.getTime() - agora.getTime()) / (1000 * 60 * 60))
    
    const novoPagamento = {
      nomePagador,
      cpfPagador,
      referenciaPagamento,
      nomeRecebedor: dadosPrefeitura.nomePrefeitura,
      cnpjRecebedor: dadosPrefeitura.cnpj,
      chavePix: dadosPrefeitura.chavePix,
      valor: valorLimpo,
      horasValidade: horasValidade > 0 ? horasValidade : 1, // Mínimo 1 hora
      dataVencimento: dataExpiracao.toISOString(),
      isNFSe,
      numeroNotaFiscal: isNFSe ? numeroNotaFiscal : "",
      isTLL,
      valorTLL: isTLL ? valorTLL : "",
      isTFF,
      valorTFF: isTFF ? valorTFF : ""
    }
    
    await addPagamento(novoPagamento)
    
    // Gerar payload PIX
    const pixPayload = gerarPixPayload(
      dadosPrefeitura?.chavePix || "",
      (dadosPrefeitura?.nomePrefeitura || "Prefeitura").substring(0, 25), // Limite de 25 caracteres
      "Sao Jose do Serido", // Cidade
      valorLimpo,
      numeroComprovante
    )
    
    // Gerar QR Code menor
    try {
      const qrCodeUrl = await QRCodeLib.toDataURL(pixPayload, {
        width: 180,
        margin: 1,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      })
      setQrCodeDataUrl(qrCodeUrl)
    } catch (error) {
      console.error("Erro ao gerar QR Code:", error)
      toast.error("Erro ao gerar QR Code")
    }
    
    setComprovanteGerado({
      ...novoPagamento,
      numeroComprovante,
      dataGeracao: new Date().toISOString(),
      dataExpiracao: dataExpiracao.toISOString(),
      pixPayload,
      isNFSe,
      numeroNotaFiscal: isNFSe ? numeroNotaFiscal : "",
      isTLL,
      valorTLL: isTLL ? valorTLL : "",
      isTFF,
      valorTFF: isTFF ? valorTFF : ""
    })
  }
  
  // Atualizar contador de tempo restante
  useEffect(() => {
    if (!comprovanteGerado?.dataExpiracao) return
    
    const interval = setInterval(() => {
      const agora = new Date()
      const expiracao = new Date(comprovanteGerado.dataExpiracao)
      const diff = expiracao.getTime() - agora.getTime()
      
      if (diff <= 0) {
        setTempoRestante("EXPIRADO")
      } else {
        const horas = Math.floor(diff / (1000 * 60 * 60))
        const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const segundos = Math.floor((diff % (1000 * 60)) / 1000)
        setTempoRestante(`${horas}h ${minutos}m ${segundos}s`)
      }
    }, 1000)
    
    return () => clearInterval(interval)
  }, [comprovanteGerado])
  
  const handleImprimirComprovante = async () => {
    if (!comprovanteGerado) return
    
    try {
      // Calcular status de validade
      let statusHtml = ""
      const agora = new Date()
      const expiracao = new Date(comprovanteGerado.dataExpiracao)
      const diff = expiracao.getTime() - agora.getTime()
      
      if (diff <= 0) {
        statusHtml = `
          <div style="background: #FEE2E2; border: 2px solid #EF4444; border-radius: 8px; padding: 12px; margin-bottom: 8px;">
            <p style="text-align: center; color: #991B1B; font-weight: bold; margin: 0;">⚠ QR CODE EXPIRADO</p>
            <p style="text-align: center; font-size: 11px; color: #991B1B; margin: 4px 0 0 0;">Este pagamento não é mais válido. Gere um novo comprovante.</p>
          </div>
        `
      } else {
        const dataExpiracao = new Date(comprovanteGerado.dataExpiracao)
        const dataFormatada = dataExpiracao.toLocaleDateString("pt-BR")
        statusHtml = `
          <div style="background: #D1FAE5; border: 3px solid #10B981; border-radius: 8px; padding: 12px; margin-bottom: 12px;">
            <p style="text-align: center; color: #065F46; font-size: 14px; font-weight: bold; margin: 0 0 6px 0;">✓ QR CODE VÁLIDO</p>
            <p style="text-align: center; font-size: 16px; color: #065F46; font-weight: bold; margin: 0;">📅 Vencimento: ${dataFormatada}</p>
            <p style="text-align: center; font-size: 11px; color: #065F46; margin: 6px 0 0 0; font-style: italic;">Válido até o fim do dia (23:59:59)</p>
          </div>
        `
      }

      // Abrir nova aba com o comprovante
      const novaAba = window.open("", "_blank")
      if (!novaAba) {
        toast.error("Bloqueador de pop-up ativo. Permita pop-ups para imprimir.")
        return
      }

      novaAba.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Talão PIX ${comprovanteGerado.numeroComprovante}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; background: #fff; }
            .talao { max-width: 800px; margin: 0 auto; background: white; border: 2px solid #333; }
            
            /* Cabeçalho Institucional */
            .cabecalho { display: flex; align-items: center; padding: 20px; border-bottom: 3px solid #333; }
            .logo-prefeitura { width: 100px; height: 100px; margin-right: 20px; flex-shrink: 0; }
            .dados-prefeitura { flex: 1; }
            .dados-prefeitura h1 { font-size: 20px; font-weight: bold; color: #004161; margin-bottom: 8px; text-transform: uppercase; border-bottom: 2px solid #004161; padding-bottom: 4px; }
            .dados-prefeitura p { font-size: 11px; color: #333; margin: 2px 0; line-height: 1.4; }
            
            /* Título do Talão */
            .titulo-talao { background: #d0d0d0; padding: 12px; text-align: center; border-bottom: 2px solid #333; }
            .titulo-talao h2 { font-size: 18px; font-weight: bold; color: #333; letter-spacing: 1px; }
            
            /* Conteúdo em 2 colunas */
            .conteudo { display: grid; grid-template-columns: 1fr 1fr; }
            
            /* Coluna Esquerda - Dados do Pagador */
            .coluna-esquerda { padding: 20px; border-right: 2px solid #333; }
            .coluna-esquerda h3 { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 15px; text-transform: uppercase; }
            .campo { margin-bottom: 12px; }
            .campo-label { font-size: 11px; font-weight: bold; color: #333; margin-bottom: 2px; }
            .campo-valor { font-size: 12px; color: #000; }
            
            /* Coluna Direita - Pagamento via PIX */
            .coluna-direita { padding: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
            .coluna-direita h3 { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 15px; text-transform: uppercase; }
            .qr-code-box { border: 3px solid #333; padding: 10px; background: white; margin-bottom: 15px; }
            .qr-code-box img { display: block; width: 200px; height: 200px; }
            .info-pagamento { width: 100%; }
            .info-pagamento .campo { margin-bottom: 8px; text-align: center; }
            .info-pagamento .campo-label { font-size: 11px; font-weight: bold; color: #333; }
            .info-pagamento .campo-valor { font-size: 13px; color: #000; font-weight: 600; }
            
            /* Rodapé */
            .rodape { padding: 12px 20px; border-top: 2px solid #333; text-align: center; background: #f5f5f5; }
            .rodape p { font-size: 10px; color: #333; margin: 2px 0; }
            
            ${diff <= 0 ? `
            .status-expirado { background: #ffcccc; border: 2px solid #cc0000; padding: 10px; margin: 10px 20px; text-align: center; }
            .status-expirado p { color: #cc0000; font-weight: bold; font-size: 12px; margin: 4px 0; }
            ` : `
            .status-valido { background: #ccffcc; border: 2px solid #00cc00; padding: 10px; margin: 10px 20px; text-align: center; }
            .status-valido p { color: #006600; font-weight: bold; font-size: 12px; margin: 4px 0; }
            `}
            
            @media print {
              body { padding: 0; background: white; }
              .talao { border: 2px solid #000; }
              @page { margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="talao">
            <!-- Cabeçalho com Logo e Dados da Prefeitura -->
            <div class="cabecalho">
              <img src="${dadosPrefeitura?.logoUrl || 'https://lumi.new/lumi.ing/logo.png'}" alt="Logo" class="logo-prefeitura" />
              <div class="dados-prefeitura">
                <h1>${dadosPrefeitura?.nomePrefeitura || 'Prefeitura Municipal'}</h1>
                <p><strong>CNPJ:</strong> ${dadosPrefeitura?.cnpj || 'Não informado'}</p>
                <p><strong>Endereço:</strong> ${dadosPrefeitura?.endereco || 'Não informado'}</p>
                <p><strong>CEP:</strong> ${dadosPrefeitura?.cep || 'Não informado'} | <strong>Tel:</strong> ${dadosPrefeitura?.telefone || 'Não informado'}</p>
                <p><strong>Site:</strong> ${dadosPrefeitura?.site || 'https://saojosedoserido.rn.gov.br/'}</p>
              </div>
            </div>
            
            <!-- Título do Talão -->
            <div class="titulo-talao">
              <h2>TALÃO DE PAGAMENTO PIX</h2>
            </div>
            
            <!-- Status de Validade -->
            ${diff <= 0 ? `
            <div class="status-expirado">
              <p>⚠ QR CODE EXPIRADO</p>
              <p style="font-size: 10px;">Este pagamento não é mais válido. Gere um novo talão.</p>
            </div>
            ` : `
            <div class="status-valido">
              <p>✓ QR CODE VÁLIDO</p>
              <p style="font-size: 10px;">Vencimento: ${new Date(comprovanteGerado.dataExpiracao).toLocaleDateString("pt-BR")} - Válido até 23:59:59</p>
            </div>
            `}
            
            <!-- Conteúdo em 2 Colunas -->
            <div class="conteudo">
              <!-- Coluna Esquerda - Dados do Pagador -->
              <div class="coluna-esquerda">
                <h3>Dados do Pagador</h3>
                <div class="campo">
                  <div class="campo-label">Nome:</div>
                  <div class="campo-valor">${comprovanteGerado.nomePagador}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">CPF:</div>
                  <div class="campo-valor">${comprovanteGerado.cpfPagador}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Referente a:</div>
                  <div class="campo-valor">${comprovanteGerado.referenciaPagamento}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Número do Comprovante:</div>
                  <div class="campo-valor">${comprovanteGerado.numeroComprovante}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Data de Emissão:</div>
                  <div class="campo-valor">${new Date(comprovanteGerado.dataGeracao).toLocaleDateString("pt-BR")} às ${new Date(comprovanteGerado.dataGeracao).toLocaleTimeString("pt-BR")}</div>
                </div>
                ${comprovanteGerado.isNFSe ? `
                <div class="campo">
                  <div class="campo-label">NFS-e - Nota Fiscal Nº:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">${comprovanteGerado.numeroNotaFiscal}</div>
                </div>
                ` : ''}
                ${comprovanteGerado.isTLL ? `
                <div class="campo">
                  <div class="campo-label">Taxa TLL:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">R$ ${comprovanteGerado.valorTLL}</div>
                </div>
                ` : ''}
                ${comprovanteGerado.isTFF ? `
                <div class="campo">
                  <div class="campo-label">Taxa TFF:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">R$ ${comprovanteGerado.valorTFF}</div>
                </div>
                ` : ''}
              </div>
              
              <!-- Coluna Direita - Pagamento via PIX -->
              <div class="coluna-direita">
                <h3>Pagamento via PIX</h3>
                <div class="qr-code-box">
                  <img src="${qrCodeDataUrl}" alt="QR Code PIX" />
                </div>
                <div class="info-pagamento">
                  <div class="campo">
                    <div class="campo-label">CHAVE PIX:</div>
                    <div class="campo-valor" style="font-size: 10px; word-break: break-all;">${comprovanteGerado.chavePix}</div>
                  </div>
                  <div class="campo">
                    <div class="campo-label">VALOR:</div>
                    <div class="campo-valor" style="font-size: 18px; color: #004161;">R$ ${comprovanteGerado.valor}</div>
                  </div>
                  <div class="campo">
                    <div class="campo-label">VENCIMENTO:</div>
                    <div class="campo-valor">${new Date(comprovanteGerado.dataExpiracao).toLocaleDateString("pt-BR")}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Código PIX Copia e Cola -->
            <div style="padding: 12px 20px; border-top: 2px solid #333; background: #fff;">
              <p style="font-size: 9px; color: #333; font-weight: bold; margin: 0 0 6px 0; text-align: center; text-transform: uppercase;">Código PIX Copia e Cola</p>
              <div style="background: #f5f5f5; border: 1px dashed #999; border-radius: 4px; padding: 6px; margin-bottom: 8px;">
                <p style="font-size: 7px; font-family: monospace; color: #333; margin: 0; word-break: break-all; line-height: 1.3;">${comprovanteGerado.pixPayload}</p>
              </div>
            </div>
            
            <!-- Rodapé -->
            <div class="rodape">
              <p><strong>Escaneie o QR Code acima com o aplicativo do seu banco para realizar o pagamento via PIX.</strong></p>
              <p>Este talão comprova a geração do pagamento. Para confirmação, envie comprovante para 84 98159-6466</p>
              <p>Código de Autenticidade: ${comprovanteGerado.numeroComprovante} | Sistema EMIT - ${dadosPrefeitura?.nomePrefeitura || 'Prefeitura'}</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
        </html>
      `)
      novaAba.document.close()
    } catch (error) {
      console.error("Erro ao gerar comprovante:", error)
      toast.error("Erro ao gerar comprovante para impressão")
    }
  }
  
  const handleNovoComprovante = () => {
    setNomePagador("")
    setCpfPagador("")
    setReferenciaPagamento("")
    setValorPagamento("")
    setDataVencimento("")
    setComprovanteGerado(null)
    setQrCodeDataUrl("")
    setTempoRestante("")
    setIsNFSe(false)
    setNumeroNotaFiscal("")
    setIsTLL(false)
    setValorTLL("")
    setIsTFF(false)
    setValorTFF("")
  }

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const apenasNumeros = e.target.value.replace(/\D/g, "")
    if (!apenasNumeros) {
      setValorPagamento("R$ 0,00")
      return
    }
    const valorNumerico = parseInt(apenasNumeros) / 100
    const valorFormatado = "R$ " + valorNumerico.toFixed(2).replace(".", ",")
    setValorPagamento(valorFormatado)
  }

  const handleValorBlur = () => {
    if (!valorPagamento || valorPagamento === "R$ 0,00") {
      setValorPagamento("R$ 0,00")
    } else if (!valorPagamento.startsWith("R$")) {
      const apenasNumeros = valorPagamento.replace(/\D/g, "")
      const valorNumerico = parseInt(apenasNumeros) / 100
      setValorPagamento("R$ " + valorNumerico.toFixed(2).replace(".", ","))
    }
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-4 md:p-8 border border-gray-200 no-print"
      >
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="p-2 md:p-3 bg-[#004161] rounded-lg">
            <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-bold text-gray-800">Gerar Pagamento</h3>
            <p className="text-xs md:text-sm text-gray-600">Sistema de pagamento municipal</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
          <div>
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Nome do Pagador *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={nomePagador}
                onChange={(e) => setNomePagador(e.target.value)}
                className="w-full pl-10 pr-4 py-3 md:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Nome completo"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              CPF ou CNPJ do Pagador *
            </label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={cpfPagador}
                onChange={handleCPFouCNPJChange}
                className="w-full pl-10 pr-4 py-3 md:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="CPF: 000.000.000-00 ou CNPJ: 00.000.000/0000-00"
                maxLength={18}
                required
              />
            </div>
            <p className="text-xs md:text-sm text-gray-500 mt-1">O sistema identifica automaticamente se é CPF (11 dígitos) ou CNPJ (14 dígitos)</p>
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Pagamento Referente a: *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={referenciaPagamento}
                onChange={(e) => setReferenciaPagamento(e.target.value)}
                className="w-full pl-10 pr-4 py-3 md:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Ex: Aforamento, Taxa..."
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Valor do Pagamento (R$) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={valorPagamento}
                onChange={handleValorChange}
                onBlur={handleValorBlur}
                className="w-full pl-10 pr-4 py-3 md:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="R$ 0,00"
                required
              />
            </div>
            <p className="text-xs md:text-sm text-gray-500 mt-1">Digite apenas o valor (ex: 10 será 10,00)</p>
          </div>
          <div>
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Data de Vencimento *
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                min={new Date().toISOString().split('T')[0]}
                value={dataVencimento}
                onChange={(e) => setDataVencimento(e.target.value)}
                className="w-full pl-10 pr-4 py-3 md:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
            <p className="text-xs md:text-sm text-gray-500 mt-1">Expira no fim do dia selecionado (23:59:59)</p>
          </div>
        </div>

        {/* Checkbox NFS-e */}
        <div className="mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isNFSe}
              onChange={(e) => {
                setIsNFSe(e.target.checked)
                if (!e.target.checked) {
                  setNumeroNotaFiscal("")
                }
              }}
              className="w-5 h-5 text-[#004161] border-gray-300 rounded focus:ring-[#004161]"
            />
            <span className="text-sm md:text-base font-medium text-gray-700">Este pagamento refere-se a uma NFS-e (Nota Fiscal de Serviços Eletrônica)</span>
          </label>
        </div>

        {/* Campo Número da Nota Fiscal - Aparece apenas se NFS-e estiver marcado */}
        {isNFSe && (
          <div className="mb-6">
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Nota Fiscal Nº *
            </label>
            <div className="relative">
              <input
                type="text"
                value={numeroNotaFiscal}
                onChange={(e) => setNumeroNotaFiscal(e.target.value)}
                className="w-full px-4 py-3 md:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Ex: 12345"
                required
              />
            </div>
            <p className="text-xs md:text-sm text-gray-500 mt-1">Número da Nota Fiscal de Serviços Eletrônica</p>
          </div>
        )}

        {/* Checkbox TLL */}
        <div className="mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isTLL}
              onChange={(e) => {
                setIsTLL(e.target.checked)
                if (!e.target.checked) {
                  setValorTLL("")
                }
              }}
              className="w-5 h-5 text-[#004161] border-gray-300 rounded focus:ring-[#004161]"
            />
            <span className="text-sm md:text-base font-medium text-gray-700">TLL (Taxa de Licença de Localização)</span>
          </label>
        </div>

        {/* Campo Valor TLL - Aparece apenas se TLL estiver marcado */}
        {isTLL && (
          <div className="mb-6">
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Valor da Taxa TLL (R$) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={valorTLL}
                onChange={(e) => {
                  let valor = e.target.value
                  valor = valor.replace(/[^\d,]/g, "")
                  if (valor.startsWith(",")) valor = "0" + valor
                  const partes = valor.split(",")
                  if (partes.length > 2) valor = partes[0] + "," + partes.slice(1).join("")
                  if (partes[1] && partes[1].length > 2) valor = partes[0] + "," + partes[1].slice(0, 2)
                  setValorTLL(valor)
                }}
                onBlur={() => {
                  if (!valorTLL) return
                  let valor = valorTLL
                  if (valor.startsWith(",")) valor = "0" + valor
                  if (!valor.includes(",")) valor = valor + ",00"
                  const partes = valor.split(",")
                  if (partes[1] && partes[1].length === 1) valor = partes[0] + "," + partes[1] + "0"
                  setValorTLL(valor)
                }}
                className="w-full pl-10 pr-4 py-3 md:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="0,00"
                required
              />
            </div>
            <p className="text-xs md:text-sm text-gray-500 mt-1">Digite o valor da taxa TLL (ex: 10 será 10,00)</p>
          </div>
        )}

        {/* Checkbox TFF */}
        <div className="mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isTFF}
              onChange={(e) => {
                setIsTFF(e.target.checked)
                if (!e.target.checked) {
                  setValorTFF("")
                }
              }}
              className="w-5 h-5 text-[#004161] border-gray-300 rounded focus:ring-[#004161]"
            />
            <span className="text-sm md:text-base font-medium text-gray-700">TFF (Taxa de Fiscalização de Funcionamento)</span>
          </label>
        </div>

        {/* Campo Valor TFF - Aparece apenas se TFF estiver marcado */}
        {isTFF && (
          <div className="mb-6">
            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2">
              Valor da Taxa TFF (R$) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={valorTFF}
                onChange={(e) => {
                  let valor = e.target.value
                  valor = valor.replace(/[^\d,]/g, "")
                  if (valor.startsWith(",")) valor = "0" + valor
                  const partes = valor.split(",")
                  if (partes.length > 2) valor = partes[0] + "," + partes.slice(1).join("")
                  if (partes[1] && partes[1].length > 2) valor = partes[0] + "," + partes[1].slice(0, 2)
                  setValorTFF(valor)
                }}
                onBlur={() => {
                  if (!valorTFF) return
                  let valor = valorTFF
                  if (valor.startsWith(",")) valor = "0" + valor
                  if (!valor.includes(",")) valor = valor + ",00"
                  const partes = valor.split(",")
                  if (partes[1] && partes[1].length === 1) valor = partes[0] + "," + partes[1] + "0"
                  setValorTFF(valor)
                }}
                className="w-full pl-10 pr-4 py-3 md:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="0,00"
                required
              />
            </div>
            <p className="text-xs md:text-sm text-gray-500 mt-1">Digite o valor da taxa TFF (ex: 10 será 10,00)</p>
          </div>
        )}

        <div className="bg-blue-50 border border-[#004161]/30 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
          <h4 className="text-sm md:text-base font-semibold text-[#004161] mb-2">Dados do Recebedor (PIX)</h4>
          <div className="space-y-1 text-xs md:text-sm text-gray-700">
            <p className="break-words"><span className="font-medium">Beneficiário:</span> {dadosPrefeitura?.nomePrefeitura}</p>
            <p className="break-words"><span className="font-medium">CNPJ:</span> {dadosPrefeitura?.cnpj}</p>
            <p className="break-all"><span className="font-medium">Chave PIX:</span> {dadosPrefeitura?.chavePix}</p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleGerarPagamento}
          className="w-full bg-gradient-to-r from-[#004161] to-[#005580] text-white font-semibold py-3 md:py-4 text-sm md:text-base rounded-lg shadow-lg hover:shadow-xl transition-all"
        >
          Gerar Comprovante de Pagamento
        </motion.button>
      </motion.div>

      {/* Comprovante Gerado - Compacto para impressão */}
      {comprovanteGerado && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-4 md:p-8 border-2 border-[#004161]"
          id="comprovante-pagamento"
        >
          <div className="text-center mb-4 md:mb-6 no-print">
            <div className="flex flex-col md:flex-row gap-2 md:gap-3 justify-center">
              <button
                onClick={handleImprimirComprovante}
                className="flex items-center justify-center gap-2 px-4 md:px-6 py-3 text-sm md:text-base bg-[#004161] text-white rounded-lg hover:bg-[#005580] transition-colors"
              >
                <Printer className="w-4 h-4 md:w-5 md:h-5" />
                Imprimir Comprovante
              </button>
              <button
                onClick={handleNovoComprovante}
                className="px-4 md:px-6 py-3 text-sm md:text-base bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Novo Pagamento
              </button>
            </div>
          </div>

          {/* Comprovante estilo bancário - Layout compacto */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 shadow-2xl">
            {/* Cabeçalho bancário - Menor */}
            <div className="bg-gradient-to-r from-[#004161] to-[#005580] rounded-t-lg p-3 -mx-4 -mt-4 mb-3">
              <div className="text-center text-white">
                <h2 className="text-lg font-bold mb-0.5">COMPROVANTE DE PAGAMENTO PIX</h2>
                <p className="text-xs opacity-90">Sistema de Arrecadação Municipal</p>
              </div>
            </div>

            {/* Status de Validade */}
            {tempoRestante === "EXPIRADO" ? (
              <div className="bg-red-100 border-2 border-red-500 rounded-lg p-3 mb-2">
                <div className="flex items-center justify-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <p className="text-red-800 font-bold uppercase">QR CODE EXPIRADO</p>
                </div>
                <p className="text-xs text-red-700 text-center mt-1">Este pagamento não é mais válido. Gere um novo comprovante.</p>
              </div>
            ) : (
              <div className="bg-green-100 border-3 border-green-500 rounded-lg p-3 mb-3 shadow-lg">
                <div className="text-center">
                  <p className="text-green-800 font-bold text-lg mb-1 flex items-center justify-center gap-2">
                    <Clock className="w-5 h-5 text-green-600" />
                    ✓ QR CODE VÁLIDO
                  </p>
                  <p className="text-green-900 font-bold text-xl mb-1">📅 Vencimento: {new Date(comprovanteGerado.dataExpiracao).toLocaleDateString("pt-BR")}</p>
                  <p className="text-xs text-green-700 italic">Válido até o fim do dia (23:59:59)</p>
                  <p className="text-xs text-green-700 mt-2 font-semibold">⏱️ Tempo restante: {tempoRestante}</p>
                </div>
              </div>
            )}
            
            {/* Número e Status - Compacto */}
            <div className="bg-white rounded-lg p-2 mb-2 shadow-md border-l-4 border-[#004161]">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Número</p>
                  <p className="text-sm font-bold text-gray-800">{comprovanteGerado.numeroComprovante}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Data e Hora</p>
                  <p className="text-xs font-semibold text-gray-800">
                    {new Date(comprovanteGerado.dataGeracao).toLocaleDateString("pt-BR")}
                  </p>
                  <p className="text-xs text-gray-600">
                    {new Date(comprovanteGerado.dataGeracao).toLocaleTimeString("pt-BR")}
                  </p>
                </div>
              </div>
            </div>

            {/* Valor Destaque - Menor */}
            <div className="bg-gradient-to-r from-[#004161] to-[#005580] rounded-lg p-2 mb-2 text-center shadow-lg">
              <p className="text-white text-xs uppercase tracking-wide mb-0.5">Valor da Transação</p>
              <p className="text-white text-xl font-bold">R$ {comprovanteGerado.valor}</p>
            </div>

            {/* Dados da Transação - Compacto */}
            <div className="bg-white rounded-lg p-2 mb-2 shadow-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Pagador */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-[#004161]" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-700 uppercase">Pagador</h3>
                  </div>
                  <div className="pl-10 space-y-1">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Nome</p>
                      <p className="text-sm font-semibold text-gray-800">{comprovanteGerado.nomePagador}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">CPF</p>
                      <p className="text-sm font-semibold text-gray-800">{comprovanteGerado.cpfPagador}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Referente a</p>
                      <p className="text-sm font-semibold text-gray-800">{comprovanteGerado.referenciaPagamento}</p>
                    </div>
                  </div>
                </div>

                {/* Recebedor */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-[#004161]" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-700 uppercase">Recebedor</h3>
                  </div>
                  <div className="pl-10 space-y-1">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Beneficiário</p>
                      <p className="text-sm font-semibold text-gray-800">{comprovanteGerado.nomeRecebedor}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">CNPJ</p>
                      <p className="text-sm font-semibold text-gray-800">{comprovanteGerado.cnpjRecebedor}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Chave PIX</p>
                      <p className="text-sm font-mono text-gray-800 break-all">{comprovanteGerado.chavePix}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code Bancário - Menor para caber na página */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <div className="text-center">
                <p className="text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">QR Code PIX</p>
                <div className={`bg-white p-1 mx-auto rounded-lg border-2 inline-block ${
                  tempoRestante === "EXPIRADO" ? "border-red-500 opacity-50" : "border-[#004161]"
                }`}>
                  {qrCodeDataUrl ? (
                    <div className="relative">
                      <img 
                        src={qrCodeDataUrl} 
                        alt="QR Code PIX" 
                        className={`w-28 h-28 mx-auto ${
                          tempoRestante === "EXPIRADO" ? "grayscale" : ""
                        }`}
                      />
                      {tempoRestante === "EXPIRADO" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 rounded">
                          <AlertTriangle className="w-12 h-12 text-red-500" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-28 h-28 bg-gray-100 flex items-center justify-center">
                      <QrCode className="w-14 h-14 text-gray-400" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-1 font-medium">
                  {tempoRestante === "EXPIRADO" 
                    ? "QR Code expirado - não pode mais ser utilizado" 
                    : "Escaneie o código acima com o app do seu banco"}
                </p>
              </div>
              
              {comprovanteGerado?.pixPayload && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide text-center">Código PIX Copia e Cola</p>
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-1">
                    <p className="text-xs font-mono break-all text-gray-700 text-center">{comprovanteGerado.pixPayload}</p>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(comprovanteGerado.pixPayload)
                      toast.success("Código PIX copiado!")
                    }}
                    className="mt-2 w-full py-1.5 bg-[#004161] text-white rounded-lg hover:bg-[#005580] transition-colors font-medium text-xs no-print"
                  >
                    Copiar Código PIX
                  </button>
                </div>
              )}
            </div>

            {/* Rodapé Institucional - Compacto */}
            <div className="mt-2 pt-2 border-t border-gray-300">
              <div className="text-center space-y-1">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-xs text-green-700 font-semibold">Comprovante Válido</p>
                </div>
                <p className="text-xs text-gray-500">Este documento comprova a geração do pagamento via PIX</p>
                <p className="text-xs text-gray-500">Para confirmar o pagamento, envie o comprovante para 84 98159-6466</p>
                <p className="text-xs text-gray-400 mt-1">Autenticidade: {comprovanteGerado.numeroComprovante}</p>
                <p className="text-xs text-gray-400">Gerado por: Sistema EMIT - {dadosPrefeitura?.nomePrefeitura}</p>
              </div>
            </div>
          </div>

          {/* Botão Novo Talão PIX - Destaque após comprovante */}
          <div className="mt-6 text-center no-print">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNovoComprovante}
              className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-lg font-semibold text-lg flex items-center gap-3 mx-auto"
            >
              <QrCode className="w-6 h-6" />
              Gerar Novo Talão PIX
            </motion.button>
            <p className="text-sm text-gray-600 mt-2">Clique para gerar um novo pagamento</p>
          </div>
        </motion.div>
      )}
    </div>
  )
}
